/**
 * NovaLauncher — global ⌘K overlay that makes Nova reachable from
 * every screen in Orbit. Reads the current route to derive context
 * so the user never has to explain where they are.
 */
import { useChat } from '@ai-sdk/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Send, X, Wrench, CheckCircle2, XCircle } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
// #437 — swap the abstract Sparkles glyph in the launcher for the
// canonical 2026 Nova avatar. Bubble = ambient idle Nova, modal
// header = smaller idle Nova. Same character everywhere.
import { NovaExpression } from '@/components/nova/kit';
import { createNovaTransport, type NovaCapability } from '@/lib/nova/context';
import { useNovaEnrichedContext } from '@/hooks/useNovaEnrichedContext';
import { useNovaIntelligence } from '@/hooks/useNovaIntelligence';
import { NovaSpinner } from '@/components/nova';

const QUICK = [
  'What should I focus on right now?',
  'Draft a follow-up to my most recent lead.',
  'Summarize this week for me.',
  'Give this page a premium redesign.',
];

export function NovaLauncher() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [novaOn] = useNovaIntelligence();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setToken(data.session?.access_token ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setToken(s?.access_token ?? null));
    return () => sub.subscription.unsubscribe();
  }, []);

  // Refs so the transport (built once) always reads fresh values.
  const tokenRef = useRef(token);
  const enriched = useNovaEnrichedContext();
  const surfaceRef = useRef(enriched);
  useEffect(() => { tokenRef.current = token; }, [token]);
  useEffect(() => { surfaceRef.current = enriched; }, [enriched]);

  const transport = useMemo(() => createNovaTransport({
    authToken: () => tokenRef.current,
    capability: () => surfaceRef.current.capability as NovaCapability,
    context: () => surfaceRef.current,
    // Persist ⌘K conversations under (user, "launcher") so they
    // survive reloads and browser sessions.
    threadSurface: () => 'launcher',
  }), []);

  const { messages, sendMessage, status, error, setMessages } = useChat({
    id: 'nova-launcher',
    transport,
  });

  // Hydrate persisted history the first time we have a token. Runs
  // once per session — nova-gateway continues appending to the same
  // thread as the user chats.
  useEffect(() => {
    if (!token || hydrated) return;
    let cancelled = false;
    (async () => {
      const { data: thread } = await supabase
        .from('nova_threads')
        .select('id')
        .eq('surface', 'launcher')
        .maybeSingle();
      if (!thread?.id) { if (!cancelled) setHydrated(true); return; }
      const { data: rows } = await supabase
        .from('nova_messages')
        .select('id, role, parts, message_ai_id, created_at')
        .eq('thread_id', thread.id)
        .order('created_at', { ascending: true })
        .limit(200);
      if (cancelled) return;
      if (rows?.length) {
        setMessages(rows.map((r) => ({
          id: r.message_ai_id ?? r.id,
          role: r.role as 'user' | 'assistant' | 'system',
          parts: (r.parts as any) ?? [],
        })) as any);
      }
      setHydrated(true);
    })();
    return () => { cancelled = true; };
  }, [token, hydrated, setMessages]);

  // ⌘K / Ctrl+K to open, Esc to close. Gated by shouldShowTrigger
  // so public / auth / portal / admin surfaces don't intercept it.
  useEffect(() => {
    // NOVA-OFF-2 — with Nova off, ⌘K shouldn't open an empty chat that
    // silently ignores you, and shouldn't preventDefault the browser's
    // own shortcut for the privilege.
    if (!novaOn || !shouldShowTrigger(location.pathname)) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, location.pathname, novaOn]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 40);
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, status]);

  const busy = status === 'submitted' || status === 'streaming';
  const surface = enriched;

  const send = (text?: string) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    // Belt to the transport's braces — createNovaTransport refuses the
    // fetch outright (NOVA-OFF-2), but failing here keeps the launcher
    // from showing a rejected-request error for a switch the user set.
    if (!novaOn) return;
    sendMessage({ text: content });
    setInput('');
  };

  // All hooks have run; nothing of hers renders with the switch off.
  if (!novaOn) return null;

  return (
    <>
      {/* Wave 10 — Floating avatar trigger killed. Nova is now
          keyboard-only (⌘K) on desktop plus the /assistant page +
          Aperture Bar tab. Modal below still opens on shortcut. */}

      {open && (
        <div className="fixed inset-0 z-[80] flex items-start justify-center bg-black/70 p-4" onClick={() => setOpen(false)}>
          <div
            className="mt-[8vh] flex h-[76vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-[rgb(18,20,24)] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* #494 rev — Editorial header: Fraunces italic
                'Ask Nova' + a tab-accent eyebrow with the current
                surface + focus so the studio knows Nova has the
                right context. */}
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
              <div className="flex items-center gap-3">
                <NovaExpression id="idle" size={40} pop={false} className="shrink-0" />
                <div>
                  <p className="orbit-eyebrow" style={{ color: 'hsl(var(--orbit-tab-accent))' }}>
                    On {surface.surface} · {surface.focus}
                  </p>
                  <p className="font-editorial text-2xl text-white leading-tight mt-0.5">
                    Ask Nova
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setOpen(false)} className="text-white/70 hover:bg-white/10 hover:text-white">
                <OrbitIcon name="xmark" fallback={X} className="h-4 w-4" />
              </Button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
              {messages.length === 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-white/70">What can I help you with?</p>
                  <div className="grid gap-1.5">
                    {QUICK.map((q) => (
                      <button
                        key={q}
                        onClick={() => send(q)}
                        className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-left text-xs text-white/80 transition hover:border-primary/40 hover:bg-white/10"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m) => (
                <div key={m.id} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                  <div
                    className={cn(
                      'max-w-[85%] whitespace-pre-wrap rounded-xl px-3.5 py-2.5 text-sm leading-relaxed',
                      m.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-white/5 text-white/90',
                    )}
                  >
                    {m.parts?.map((p: any, i: number) => {
                      if (p.type === 'text') return <span key={i}>{p.text}</span>;
                      if (typeof p.type === 'string' && p.type.startsWith('tool-')) {
                        return <ToolChip key={i} part={p} />;
                      }
                      return null;
                    })}
                  </div>
                </div>
              ))}

              {status === 'submitted' && (
                <div className="flex items-center gap-2 text-xs text-white/50">
                  <NovaSpinner size={12} /> Nova is thinking…
                </div>
              )}
              {error && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2.5 text-xs text-destructive">
                  {error.message}
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="border-t border-white/10 p-3">
              <div className="flex items-end gap-2">
                <Textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder={`Ask Nova about ${surface.surface.toLowerCase()}…`}
                  className="min-h-[56px] resize-none border-white/10 bg-white/5 text-sm text-white placeholder:text-white/40 focus-visible:ring-primary/40"
                  disabled={busy || !token}
                />
                <Button
                  size="icon"
                  onClick={() => send()}
                  disabled={!input.trim() || busy || !token}
                  className="shrink-0"
                >
                  {busy ? <NovaSpinner size={16} /> : <OrbitIcon name="paperplane.fill" fallback={Send} className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function shouldShowTrigger(pathname: string): boolean {
  // #494 rev — Also hidden on /assistant (the full-screen Nova
  // chat) since the launcher would duplicate the surface.  And
  // /nova-design for the same reason.
  const hidden = ['/auth', '/portal/', '/inquiry-portal/', '/embed/', '/sign/', '/d/', '/book/', '/s/', '/video/', '/invite/', '/delivery/', '/admin', '/onboarding', '/welcome', '/nova-intro', '/tour', '/m/', '/assistant', '/nova-design'];
  if (pathname === '/m') return false;
  return !hidden.some((h) => pathname === h || pathname.startsWith(h));
}

export default NovaLauncher;

/** Inline tool-activity chip rendered inside assistant bubbles. */
function ToolChip({ part }: { part: any }) {
  const name: string = String(part.type ?? '').replace(/^tool-/, '');
  const state: string = part.state ?? '';
  const ok = part.output?.ok !== false;
  const done = state === 'output-available';
  return (
    <div
      className={cn(
        'my-1 flex items-center gap-2 rounded-md border px-2 py-1 text-[11px]',
        done && ok && 'border-primary/30 bg-primary/10 text-white/85',
        done && !ok && 'border-destructive/40 bg-destructive/10 text-destructive',
        !done && 'border-white/10 bg-white/5 text-white/70',
      )}
    >
      {!done ? (
        <NovaSpinner size={12} className="shrink-0" />
      ) : ok ? (
        <OrbitIcon name="checkmark.circle.fill" fallback={CheckCircle2} className="h-3 w-3 shrink-0" />
      ) : (
        <OrbitIcon name="xmark.circle.fill" fallback={XCircle} className="h-3 w-3 shrink-0" />
      )}
      <OrbitIcon name="wrench.adjustable" fallback={Wrench} className="h-3 w-3 shrink-0 opacity-60" />
      <span className="font-medium">{humanizeToolName(name)}</span>
    </div>
  );
}

function humanizeToolName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}